import React, { FC, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import PageTitle from 'components/shared/page-title/page-title.component';
import { Alert, Box, Button, Card, CardActionArea, CardContent, Grid, TextField, Typography } from '@mui/material';
import { useState, useEffect } from 'react';
import { useMutation } from '@apollo/client';
import { modifyUserAccountSettingsTemplate } from 'apollo/templates/user/mutation.graphql';
import { currentUserVar } from 'apollo/reactive-variables/user';
import AlertSnackbar from 'components/shared/alert-snackbar/alert-snackbar.component';
import googleAuthLogo from 'assets/google-authenticator-logo.png';
import EnableEidDialog, { EID_METHODS } from './dialogs/enable-eid-dialog.component';
import EnableGoogleAuthDialog from './dialogs/enable-google-auth-dialog.component';
import EidMark from './eid-mark.component';
import DisableGoogleAuthDialog from './dialogs/disable-google-auth-dialog.component';
import ChangePasswordDialog from './dialogs/change-password-dialog.component';
import { nameRegExp } from 'config/regex';
import { AuthMethod, isEidErrorCode, isIduraAuthMethod } from 'models/user';
import { isEidMethodEnabled } from 'utils/enabled-eid-methods';
import { useSearchParams } from 'react-router-dom';

// The e-IDs a user may connect in this build (REACT_APP_ENABLED_EID_METHODS). Idura's
// production agreements arrive one country at a time; a card for an e-ID without one
// could only end in an error. Existing bindings are unaffected — see enabled-eid-methods.
const ENABLED_EID_OPTIONS = EID_METHODS.filter(({ method }) => isEidMethodEnabled(method));

const AccountSettings: FC = () => {
    const [changesSaved, setChangesSaved] = useState(false);
    const currentUser = currentUserVar();
    const { t: translate } = useTranslation(['user-settings', 'common']);
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [isFirstNameValid, setIsFirstNameValid] = useState<boolean>(false);
    const [isLastNameValid, setIsLastNameValid] = useState<boolean>(false);
    const [enableEidMethod, setEnableEidMethod] = useState<AuthMethod | null>(null);
    const [isEnableGoogleAuthOpen, setIsEnableGoogleAuthOpen] = useState(false);
    const [isDisableGoogleAuthOpen, setIsDisableGoogleAuthOpen] = useState(false);
    const [isChangePasswordOpen, setIsChangePasswordOpen] = useState(false);

    // Enrolment finishes on Idura's side and returns here through a full page load, so
    // the only way to tell the user it worked is the parameter the callback appends.
    const [searchParams, setSearchParams] = useSearchParams();
    const enrolledMethod = searchParams.get('eidEnrolled');
    // ...and a failed one comes back with a code. Only codes we know are rendered.
    const eidErrorParam = searchParams.get('eidError');
    const eidError = isEidErrorCode(eidErrorParam) ? eidErrorParam : null;

    // Password users can change their password. e-ID and SSO users authenticate without
    // a Denthub password, so the option is hidden for them. 2FA (TOTP / Google
    // Authenticator) is a second factor on top of a password, so those users DO have a
    // password and are intentionally not excluded here.
    //
    // DD-3420: authMethod is the only answer to how a user logs in; the flags that used to
    // be combined for this disagreed with each other often enough to be worthless.
    const usesPassword = currentUser.authMethod === AuthMethod.PASSWORD;
    const hasEid = isIduraAuthMethod(currentUser.authMethod);

    useEffect(() => {
        setFirstName(currentUser.name.firstName || '');
        setLastName(currentUser.name.lastName || '');
    }, [currentUser]);

    const [modifyUserAccountSettings, { error }] = useMutation(modifyUserAccountSettingsTemplate, {
        onCompleted: ({ modifyUserAccountSettings }) => {
            currentUser.name.firstName = modifyUserAccountSettings.name.firstName;
            currentUser.name.lastName = modifyUserAccountSettings.name.lastName;
            currentUserVar(currentUser);
            setChangesSaved(true);
        },
    });

    const handleSaveChanges = async () => {
        if (firstName && lastName) {
            await modifyUserAccountSettings({
                variables: {
                    firstName,
                    lastName,
                },
            });
        }
    };

    return (
        <Fragment>
            {error ? <AlertSnackbar type="error" message={error?.message} /> : undefined}
            {changesSaved ? <AlertSnackbar type="success" message={translate('common:CHANGES_SAVED')} /> : undefined}
            {enrolledMethod ? (
                <AlertSnackbar
                    type="success"
                    message={translate('user-settings:EID_ENROLLED')}
                    onClose={() => setSearchParams({}, { replace: true })}
                />
            ) : undefined}
            {eidError ? (
                <AlertSnackbar
                    type="error"
                    message={translate(`user-settings:${eidError}`)}
                    onClose={() => setSearchParams({}, { replace: true })}
                />
            ) : undefined}
            <PageTitle headingStyle="h2" title={translate('user-settings:ACCOUNT_SETTINGS')} />

            <Box mb={2}>
                <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                        <Typography variant="h4" sx={{ marginBottom: 1 }}>
                            {translate('common:FIRST_NAME')}
                        </Typography>
                        <TextField
                            fullWidth
                            value={firstName}
                            onChange={(event) => {
                                const firstName = event.target.value;
                                setIsFirstNameValid(nameRegExp.test(firstName));
                                setFirstName(event.target.value);
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Typography variant="h4" sx={{ marginBottom: 1 }}>
                            {translate('common:LAST_NAME')}
                        </Typography>
                        <TextField
                            fullWidth
                            value={lastName}
                            onChange={(event) => {
                                const lastName = event.target.value;
                                setIsLastNameValid(nameRegExp.test(lastName));
                                setLastName(event.target.value);
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        {/* syncid doubles as the generic SSO subject, so keying on it labelled
                            every company-SSO user's IdP subject as a SITHS card number — an
                            OIDC user saw a Keycloak UUID under "SITHS eID". Only a SITHS user
                            has a syncid worth showing. */}
                        {currentUser.authMethod === AuthMethod.SITHS && currentUser.syncid ? (
                            <Fragment>
                                <Typography variant="h4" sx={{ marginBottom: 1 }}>
                                    {translate('user-settings:SITHS_ID')}
                                </Typography>
                                <TextField fullWidth value={currentUser.syncid} disabled />
                            </Fragment>
                        ) : (
                            <Fragment>
                                <Typography variant="h4" sx={{ marginBottom: 1 }}>
                                    {translate('common:PERSONAL_IDENTITY_NUMBER')}
                                </Typography>
                                <TextField fullWidth value={currentUser.personalIdentityNumber} disabled />
                            </Fragment>
                        )}
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Typography variant="h4" sx={{ marginBottom: 1 }}>
                            {translate('common:EMAIL')}
                        </Typography>
                        <TextField fullWidth value={currentUser.email} disabled />
                    </Grid>
                </Grid>
            </Box>

            <Box mb={4}>
                <Button
                    onClick={handleSaveChanges}
                    variant="contained"
                    disabled={!firstName || !lastName || !isFirstNameValid || !isLastNameValid}
                >
                    {translate('user-settings:SAVE_CHANGES')}
                </Button>
            </Box>
            <Box>
                <Typography variant="h3" sx={{ marginBottom: 1 }}>
                    {translate('user-settings:LOGIN_METHOD')}
                </Typography>

                {usesPassword && !currentUser.isTotpEnabled ? (
                    <Box>
                        <Box mb={2}>
                            <Alert severity="info">{translate('user-settings:YOU_ARE_USING_PASSWORD_INFO')}</Alert>
                        </Box>
                        <Box>
                            <Grid container spacing={2}>
                                {/* One card per e-ID rather than a single card behind a
                                        picker: the user knows which e-ID they hold, so making
                                        them open a dialog to say so is a step for nothing. */}
                                {ENABLED_EID_OPTIONS.map(({ method, labelKey }) => (
                                    <Grid item sm={12} md={4} key={method}>
                                        <Card variant="outlined" sx={{ height: '100%' }}>
                                            <CardActionArea
                                                onClick={() => setEnableEidMethod(method)}
                                                sx={{ height: '100%' }}
                                                data-test={`account-settings.enable-${method.toLowerCase()}`}
                                            >
                                                <CardContent>
                                                    <Box display="flex" alignItems="center">
                                                        <EidMark method={method} alt={translate(labelKey)} />
                                                        <Typography variant="h3" component="div">
                                                            {translate(labelKey)}
                                                        </Typography>
                                                    </Box>
                                                </CardContent>
                                            </CardActionArea>
                                        </Card>
                                    </Grid>
                                ))}
                                <Grid item sm={12} md={4}>
                                    <Card variant="outlined" sx={{ height: '100%' }}>
                                        <CardActionArea
                                            onClick={() => setIsEnableGoogleAuthOpen(true)}
                                            sx={{ height: '100%' }}
                                        >
                                            <CardContent>
                                                <Box display="flex" alignItems="center">
                                                    <img
                                                        alt="Google auth logo"
                                                        style={{ height: 50 }}
                                                        src={googleAuthLogo}
                                                    />
                                                    <Box ml={2}>
                                                        <Typography variant="h3" component="div">
                                                            {translate('user-settings:GOOGLE_AUTH')}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            </CardContent>
                                        </CardActionArea>
                                    </Card>
                                </Grid>
                            </Grid>
                        </Box>
                    </Box>
                ) : undefined}

                {currentUser.authMethod === AuthMethod.INTEGRATION ? (
                    <Box>
                        <Alert severity="info">{translate('user-settings:YOU_ARE_USING_INTEGRATION_INFO')}</Alert>
                    </Box>
                ) : null}
                {currentUser.authMethod === AuthMethod.SITHS ? (
                    <Box>
                        <Alert severity="info">{translate('user-settings:YOU_ARE_USING_SITHS_INFO')}</Alert>
                    </Box>
                ) : undefined}
                {currentUser.authMethod === AuthMethod.SAML || currentUser.authMethod === AuthMethod.OIDC ? (
                    <Box>
                        <Alert severity="info">{translate('user-settings:YOU_ARE_USING_SSO_INFO')}</Alert>
                    </Box>
                ) : undefined}
                {hasEid ? (
                    <Box>
                        <Box mb={1}>
                            <Alert severity="info">
                                {translate(`user-settings:YOU_ARE_USING_${currentUser.authMethod}_INFO`)}
                            </Alert>
                        </Box>
                        {/* DD-3552: BankID may not be used to create or restore another
                                login method, so there is no Disconnect here. Support changes
                                it after checking who the user is without the e-ID. */}
                        <Alert severity="info" data-test="account-settings.eid-change-via-support">
                            {translate('user-settings:EID_CHANGE_VIA_SUPPORT')}
                        </Alert>
                    </Box>
                ) : undefined}
                {currentUser.isTotpEnabled ? (
                    <Box>
                        <Box mb={1}>
                            <Alert severity="info">{translate('user-settings:YOU_ARE_USING_GOOGLE_AUTH_INFO')}</Alert>
                        </Box>
                        <Button color="warning" onClick={() => setIsDisableGoogleAuthOpen(true)}>
                            {translate('user-settings:DISABLE_GOOGLE_AUTH')}
                        </Button>
                    </Box>
                ) : undefined}
            </Box>

            {usesPassword ? (
                <Box mt={4}>
                    <Typography variant="h3" sx={{ marginBottom: 1 }}>
                        {translate('user-settings:CHANGE_PASSWORD')}
                    </Typography>
                    <Box mb={2}>
                        <Alert severity="info">{translate('user-settings:CHANGE_PASSWORD_DESC')}</Alert>
                    </Box>
                    <Button variant="text" onClick={() => setIsChangePasswordOpen(true)}>
                        {translate('user-settings:CHANGE_PASSWORD')}
                    </Button>
                </Box>
            ) : undefined}

            <ChangePasswordDialog open={isChangePasswordOpen} onClose={() => setIsChangePasswordOpen(false)} />
            <EnableEidDialog method={enableEidMethod} onClose={() => setEnableEidMethod(null)} user={currentUser} />
            <EnableGoogleAuthDialog open={isEnableGoogleAuthOpen} onClose={() => setIsEnableGoogleAuthOpen(false)} />
            <DisableGoogleAuthDialog open={isDisableGoogleAuthOpen} onClose={() => setIsDisableGoogleAuthOpen(false)} />
        </Fragment>
    );
};

export default AccountSettings;
